import {defineStore} from 'pinia'
import {BoardsNs, EstimationNs, FeatureNs, ProjectNs, WorkspaceNs} from "@shared/types";
import {cookies, getBaseDomain, getLocalStorage, setLocalStorage} from "@shared/helpers";
import {PublicNs} from "@shared/types/public.namespace.ts";
import {PublicEstimationApi} from "@/api/public-estimation.apis.ts";
import {useFeatureStore} from "@domains/boards";
import {EstimationApi} from "@/api";


export const usePublicEstimationStore = defineStore("publicEstimation", {
  state: () => ({
    token: '',
    password: '',
    publishInfo: {} as ProjectNs.PublishProjectResponse,
    workspaceData: {} as WorkspaceNs.Workspace,
    estimation: {} as EstimationNs.ProjectEstimation,
    recentlyViewed: [] as BoardsNs.RecentlyViewed[],
    isSidebarOpen: false,
    isSitemapOpen: false,

    featureMap: new Map<string, EstimationNs.FeatureEstimation>,
    featureTree: [] as EstimationNs.EstimationTreeNode[],
    selectedGroup: undefined as EstimationNs.EstimationGroup | undefined,
    selectedFeatureEstimation: undefined as EstimationNs.FeatureEstimation | undefined,
    groupSummary: [] as EstimationNs.GroupSummary[],
    activeDetailTab: 'feature' as 'feature' | 'screens',
    isTreeExpanded: true
  }),
  getters: {
    getPublishInfo: (state) => state.publishInfo,
    getWorkspaceData: (state) => state.workspaceData,
    getProjectId: (state) => state.estimation.projectId,
    getProjectName: (state) => state.estimation.projectName,
    getSelectedEstimation: (state) => state.estimation,
    getSelectedGroup: (state) => state.selectedGroup,
    getGroups: (state) => state.estimation?.groups,
    getActiveDetailTab: (state) => state.activeDetailTab,
    getIsTreeExpanded: (state) => state.isTreeExpanded,
    getSelectedFeatureEstimation: (state) => state.selectedFeatureEstimation,
    getGroupSummaryData: (state) => state.groupSummary,

    getRecentlyViewedByCurrentProject: (state) => {
      let rViewed = state.recentlyViewed;
      if (!rViewed || rViewed.length === 0) {
        rViewed = getLocalStorage('recentlyViewed');
      }
      if (rViewed && rViewed.length > 0 && state.estimation.projectId) {
        return rViewed.find((r) => r.projectId === state.estimation.projectId);
      }
      return {} as BoardsNs.RecentlyViewed;
    }
  },
  actions: {
    convertFlatDataToTree() {
      const map = new Map<string, EstimationNs.EstimationTreeNode>;

      this.featureMap.forEach((item: EstimationNs.FeatureEstimation, key: string) => {
        // Spread properties like feature store does to make color directly accessible
        map.set(key, {key, data: {...item, ...(item as any).properties}, children: []} as EstimationNs.EstimationTreeNode);
      });

      const hierarchy: EstimationNs.EstimationTreeNode[] = [];
      map.forEach((item: EstimationNs.EstimationTreeNode, key: string) => {
        if (item.data.parentId) {
          const parentItem = map.get(item.data.parentId);
          if (parentItem) {
            // Find parent color for inheritance (like feature store)
            const parentColor = (parentItem.data as any).parentColor ?? (parentItem.data as any).color;
            if (parentColor && !(item.data as any).parentColor) {
              (item.data as any).parentColor = parentColor;
            }
            parentItem.children!.push(item!);
          }
        } else {
          hierarchy.push(item!);
        }
      });

      this.featureTree = hierarchy;
      return hierarchy;
    },

    async checkToken(projectId: string, token: string): Promise<ProjectNs.PublishProjectResponse> {
      const payload = {projectId, token} as PublicNs.CheckTokenRequest;
      return PublicEstimationApi.checkToken(payload).then((publishInfo) => {
        this.publishInfo = publishInfo;
        return publishInfo;
      })
    },

    async loadProjectData(projectId: string, token: string, password: string): Promise<PublicNs.PublicEstimationData> {
      const payload = {projectId, token, password} as PublicNs.LoadProjectDataRequest;
      return PublicEstimationApi.loadProjectData(payload).then((response) => {
        this.workspaceData = response.workspace;
        this.estimation = response.estimationProject;
        this.groupSummary = response.groupSummary;
        this.token = token;
        this.password = password;

        if (password && password.length > 0) {
          cookies.set(`ep-${projectId}`, password, {domain: getBaseDomain});
        }

        return response;
      })
    },

    // Load estimation data for a project
    async loadFeatureEstimationData() {
      const payload = {
        projectId: this.estimation.projectId,
        token: this.token,
        password: this.password,
        groupId: this.selectedGroup?.groupId
      } as PublicNs.LoadProjectDataRequest;

      PublicEstimationApi.loadFeatureData(payload).then((featureList) => {
        this.featureMap.clear();
        featureList.forEach(item => {
          this.featureMap.set(item.featureId, item as any);
        });
        this.convertFlatDataToTree();
        return featureList;
      });
    },

    // Set selected feature estimation
    setSelectedFeatureEstimation(feature: EstimationNs.FeatureEstimation | undefined) {
      this.selectedFeatureEstimation = feature;
    },

    // Set selected feature active detail tab
    setActiveDetailTab(activeTab: 'feature' | 'screens' | 'feature') {
      this.activeDetailTab = activeTab;
    },

    addItemToRecentlyViewed(payload: { type: 'FEATURE' | 'SCREEN', item: any }): void {
      let rViewed = this.recentlyViewed;
      if (!rViewed || rViewed.length === 0) {
        rViewed = getLocalStorage('recentlyViewed');
        if (!rViewed) {
          rViewed = [] as BoardsNs.RecentlyViewed[];
        }
      }

      let projectViewed;
      if (rViewed && rViewed.length > 0 && this.estimation.projectId) {
        projectViewed = rViewed.find((r) => r.projectId === this.estimation.projectId);
      }

      if (!projectViewed) {
        projectViewed = {
          projectId: this.estimation.projectId,
          features: [] as BoardsNs.RecentlyViewedItem[],
          screens: [] as BoardsNs.RecentlyViewedItem[]
        } as BoardsNs.RecentlyViewed;
        rViewed.push(projectViewed);
      }

      if (payload.type === 'FEATURE') {
        const index = projectViewed.features.findIndex((r: BoardsNs.RecentlyViewedItem) => r.id === payload.item.id);
        if (index > -1) {
          projectViewed.features.splice(index, 1);
        }
        projectViewed.features.unshift({
          id: payload.item.id,
          type: payload.item.featureType,
          summary: payload.item.summary,
          color: payload.item.properties?.color
        });
        projectViewed.features = projectViewed.features.splice(0, 5);
      } else {
        const index = projectViewed.screens.findIndex((r: BoardsNs.RecentlyViewedItem) => r.id === payload.item.id);
        if (index > -1) {
          projectViewed.screens.splice(index, 1);
        }
        projectViewed.screens.unshift({
          id: payload.item.id,
          type: payload.item.screenType,
          summary: payload.item.summary,
          color: payload.item.color
        });
        projectViewed.screens = projectViewed.screens.splice(0, 5);
      }

      this.recentlyViewed = rViewed;
      setLocalStorage('recentlyViewed', rViewed);
    },
    toggleSidebar() {
      this.isSidebarOpen = !this.isSidebarOpen;
    },
    toggleSitemap() {
      this.isSitemapOpen = !this.isSitemapOpen;
    },
    closeSitemap() {
      this.isSitemapOpen = false;
    },
    // Toggle tree expand/collapse
    toggleTreeExpanded() {
      this.isTreeExpanded = !this.isTreeExpanded;
    }
  }
})
